import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes, css } from 'styled-components';
import type { RootState } from '../../../../state/store';
import { useConversation, type FollowupMeta } from '../../hooks/useConversation';
import { useSampleQuestions } from '../../hooks/useSampleQuestions';
import { useTokenUsage } from '../../hooks/useTokenUsage';
import AccessDeniedView from '../AccessDeniedView';
import ConversationSidebar from '../ConversationSidebar';
import UserMessage from '../UserMessage';
import AskVantageResponse, { AV_PROFILE_OPEN_EVENT, type ProfileOpenDetail } from '../AskVantageResponse';
import BusinessProfilePanel from '../BusinessProfilePanel';
import MessageInput from '../MessageInput';
import ThinkingIndicator from '../ThinkingIndicator';
import SkeletonLoader from '../SkeletonLoader';
import { TokenMeter } from '../TokenMeter';
import { TokenPlansModal } from '../../../askVantage/components/TokenPlansModal';
import { MessageVote } from '../../services/api';
import askVantageLogo from '../../../../images/ask_vantage_logo.svg';
import askVantageLogoDark from '../../../../images/ask_vantage_logo_dark.svg';
import { brandGradient, contentColumnPadding, heroAmbientBackground, ASK_VANTAGE_HOME_EVENT, av2ResponseLightModeHtmlFix, type ThemeMode } from '../../theme/constants';
import { useAv2ThemeMode } from '../../theme/hooks';
import { useExtAConfigs } from '../../../extendedASSRM/hooks/useExtAConfigs';
import type { ExtAContextTypes } from '../../../extendedASSRM/types/extendedA';
import {
  buildDarUrl,
  deepLinkFiltersFromParams,
  resolveDarSegments
} from '../../utils/buildDarUrl';
import { exportConversation } from '../../helpers/exportUtils';

const AskV2Container: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTokenPlansModal, setShowTokenPlansModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [profileInitialCategory, setProfileInitialCategory] = useState<string | undefined>(undefined);
  const [profileFocus, setProfileFocus] = useState<ProfileOpenDetail | undefined>(undefined);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const assistantResponseTopRef = useRef<HTMLDivElement>(null);

  const companyDetails = useSelector(
    (state: RootState) => state.company?.company_details as any
  );
  const hasMaintenance = companyDetails?.maintenance_mode?.enabled || false;
  const themeMode = useAv2ThemeMode();
  const askLogo = themeMode === 'dark' ? askVantageLogoDark : askVantageLogo;

  const {
    state,
    responseContainerRef,
    loadConversations,
    startNewConversation,
    selectConversation,
    removeConversation,
    submitMessage,
    submitFollowup,
    voteMessage,
  } = useConversation();

  const { dialogHandler: extADialog } = useExtAConfigs();

  const { conversations, activeConversationId, messages, streamStatus, thinkingText, error, conversationsStatus } =
    state;

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // LLM-rendered [data-av-profile-open] buttons dispatch a window event — we
  // listen here so the central Business Profile panel opens on the correct
  // category when the user clicks the in-chat deep link.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ProfileOpenDetail>).detail;
      setProfileInitialCategory(detail?.category);
      setProfileFocus(detail);
      setShowProfilePanel(true);
    };
    window.addEventListener(AV_PROFILE_OPEN_EVENT, handler);
    return () => window.removeEventListener(AV_PROFILE_OPEN_EVENT, handler);
  }, []);

  const {
    tokenUsage,
    loading: tokenUsageLoading,
    isDemo: isDemoAccount,
    refresh: refreshTokenUsage,
  } = useTokenUsage(conversationsStatus === 'loaded');

  // After every assistant turn completes, refetch usage so the meter reflects
  // consumption immediately. The `token_balance_updated` Pusher event will
  // also fire but can land slightly later — this guarantees zero lag.
  useEffect(() => {
    if (streamStatus === 'done') {
      void refreshTokenUsage();
    }
  }, [streamStatus, refreshTokenUsage]);

  // Scroll last user message into view on submit
  useEffect(() => {
    if (streamStatus === 'thinking' || streamStatus === 'streaming') {
      lastUserMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [streamStatus]);

  // When the assistant answer is ready, scroll so the start of that response is visible (not the bottom)
  useEffect(() => {
    if (streamStatus !== 'done') return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        assistantResponseTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    return () => cancelAnimationFrame(id);
  }, [streamStatus, messages.length]);

  const handleSend = useCallback(
    (content: string) => {
      submitMessage(content, activeConversationId ?? undefined);
    },
    [submitMessage, activeConversationId]
  );

  const handleFollowup = useCallback(
    (question: string, meta?: FollowupMeta) => {
      void submitFollowup(question, meta);
    },
    [submitFollowup]
  );

  const handleVantageLink = useCallback(
    (entity: string, params: Record<string, string>) => {
      if (entity === 'extended_analysis') {
        const resolved = resolveDarSegments('extended_analysis', params);
        if (resolved?.analytics_widget_name) {
          const open: Parameters<ExtAContextTypes['dialogHandler']['showReport']>[0] = {
            widgetUrl: resolved.analytics_widget_name,
            dashboard: resolved.dashboard_name,
            dashboardWidget: resolved.dashboard_widget_name,
            ...deepLinkFiltersFromParams(params)
          };
          extADialog.showReport(open);
          return;
        }
      }
      const url = buildDarUrl(entity, params);
      if (url) {
        window.open(url, '_blank');
      } else {
        const label = params._label || `Show me the full ${entity} analysis`;
        submitFollowup(label);
      }
    },
    [submitFollowup, extADialog]
  );

  const handleBuyTokens = useCallback(() => {
    setShowTokenPlansModal(true);
  }, []);

  const handleVote = useCallback(
    (messageId: string | number, vote: MessageVote) => {
      void voteMessage(messageId, vote);
    },
    [voteMessage]
  );

  const handleExport = useCallback(async () => {
    if (messages.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const conversation = conversations.find((c) => c.id === activeConversationId);
      const title = conversation?.title || 'Ask Vantage Conversation';
      const username = (companyDetails as any)?.user_name || '';
      await exportConversation(messages, title, username);
    } finally {
      setIsExporting(false);
    }
  }, [messages, conversations, activeConversationId, companyDetails, isExporting]);

  const handleNewConversation = useCallback(async () => {
    await startNewConversation();
    setSidebarOpen(false);
    setShowProfilePanel(false);
  }, [startNewConversation]);

  // Navbar "Ask Vantage" re-click while on /ask returns to the welcome screen.
  useEffect(() => {
    const handler = () => {
      void handleNewConversation();
    };
    window.addEventListener(ASK_VANTAGE_HOME_EVENT, handler);
    return () => window.removeEventListener(ASK_VANTAGE_HOME_EVENT, handler);
  }, [handleNewConversation]);

  const handleSelectConversation = useCallback(
    async (id: number) => {
      await selectConversation(id);
      setSidebarOpen(false);
    },
    [selectConversation]
  );

  const isStreaming =
    streamStatus === 'thinking' || streamStatus === 'streaming' || streamStatus === 'finalizing';
  const isInputDisabled = isStreaming;

  const userMessages = messages.filter((m) => m.message_type === 'user');
  const lastUserMsg = userMessages[userMessages.length - 1];

  // Don't show the welcome screen until the initial conversations fetch completes.
  // This prevents a flash of the home page before a 403 is detected.
  const showWelcome = conversationsStatus === 'loaded' && messages.length === 0 && !isStreaming;

  // Fetch a large random mixed-domain pool once per mount. The first 6 are
  // shown as static chips (stable for the session); the full pool feeds the
  // animated placeholder so it has as much variety as possible.
  const { questions: sampleQuestions } = useSampleQuestions(20);
  const chipQuestions = useMemo(
    () => sampleQuestions.slice(0, 6).map((q) => q.question),
    [sampleQuestions]
  );
  const placeholderQuestions = useMemo(
    () => sampleQuestions.map((q) => q.question),
    [sampleQuestions]
  );

  const userFirstName =
    ((companyDetails?.user_name as string | undefined) || '').trim().split(/\s+/)[0] || '';
  const companyName = (companyDetails?.company_name as string | undefined) || '';

  // All hooks have been called above — safe to return early here.
  // loadConversations caught a 403 (staff permission denied) and set state.error.
  if (error) {
    return <AccessDeniedView message={error} />;
  }

  return (
    <PageWrapper $hasMaintenance={hasMaintenance}>
      <TokenPlansModal show={showTokenPlansModal} onClose={() => setShowTokenPlansModal(false)} />
      <BusinessProfilePanel
        show={showProfilePanel}
        onClose={() => setShowProfilePanel(false)}
        initialCategory={profileInitialCategory}
        initialFocus={profileFocus}
      />

      <ConversationSidebar
        conversations={conversations}
        activeConversationId={activeConversationId}
        onSelect={handleSelectConversation}
        onNew={handleNewConversation}
        onDelete={removeConversation}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <MainArea>
        {/* Top bar — three-column grid keeps the logo visually centered
            regardless of how many controls the right column holds. The logo
            also fades out when the hero is showing (where a larger logo sits
            above the greeting instead). */}
        <TopBar>
          <TopBarLeft>
            <SidebarToggle
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle conversation history"
            >
              <HamburgerIcon />
            </SidebarToggle>
          </TopBarLeft>
          <TopBarCenter>
            <TopBarLogo
              src={askLogo}
              alt="Ask Vantage"
              $show={!showWelcome}
              aria-hidden={showWelcome}
            />
          </TopBarCenter>
          <TopBarRight>
            <ProfileButton
              onClick={() => {
                setProfileInitialCategory(undefined);
                setShowProfilePanel(true);
              }}
              aria-label="Open KPIs and Business Parameters"
              title="Configure KPIs and Business Parameters"
            >
              KPIs and Business Parameters
            </ProfileButton>
            <TokenMeter
              variant="compact"
              stats={tokenUsage}
              loading={tokenUsageLoading}
              isDemo={isDemoAccount}
              show={!showWelcome}
            />
            {messages.length > 0 && (
              <ExportButton
                onClick={handleExport}
                disabled={isExporting}
                aria-label={isExporting ? 'Preparing export…' : 'Export to PDF'}
                title={isExporting ? 'Preparing export…' : 'Export / Print conversation'}
              >
                <PrintIcon />
              </ExportButton>
            )}
            {activeConversationId && (
              <NewChatTopBtn
                onClick={handleNewConversation}
                aria-label="Start new conversation"
              >
                + New
              </NewChatTopBtn>
            )}
          </TopBarRight>
        </TopBar>

        {/* Messages scroll area — collapses to 0 height in landing mode so the
            centered hero below becomes the focal point. */}
        {!showWelcome && (
          <MessagesArea>
            <MessagesInner>
              {/* Render completed message pairs */}
              {renderMessagePairs(
                messages,
                lastUserMsg?.id,
                isStreaming,
                state,
                handleFollowup,
                handleVantageLink,
                handleBuyTokens,
                handleVote,
                activeConversationId,
                lastUserMsgRef,
                responseContainerRef,
                assistantResponseTopRef,
              )}

              {/* Streaming response */}
              {isStreaming && (
                <>
                  {streamStatus === 'thinking' && (
                    <>
                      <ThinkingIndicator text={thinkingText || 'Thinking...'} />
                      <SkeletonLoader />
                    </>
                  )}
                  {(streamStatus === 'streaming' || streamStatus === 'finalizing') && (
                    <StreamingArea ref={responseContainerRef} className="av2-response" />
                  )}
                  {(streamStatus === 'streaming' || streamStatus === 'finalizing') && (
                    <LiveStreamFooter>
                      <LiveDots>
                        <LiveDot $delay={0} />
                        <LiveDot $delay={0.2} />
                        <LiveDot $delay={0.4} />
                      </LiveDots>
                      <LiveText>
                        {streamStatus === 'finalizing'
                          ? thinkingText || 'Finalizing response...'
                          : 'Generating answer...'}
                      </LiveText>
                    </LiveStreamFooter>
                  )}
                </>
              )}
            </MessagesInner>
          </MessagesArea>
        )}

        {/* Input — centered hero when showWelcome, bottom-pinned during chat.
            Hero header + suggestion chips stay mounted and fade/collapse via CSS
            so the transition works cleanly in both directions. */}
        <InputArea $hero={showWelcome}>
          <HeroHeader $show={showWelcome} aria-hidden={!showWelcome}>
            <HeroLogo src={askLogo} alt="Ask Vantage" />
            <HeroGreeting>
              {buildGreeting(userFirstName)}
            </HeroGreeting>
            <HeroSubtitle>
              {companyName ? (
                <>
                  What can I help you with at{' '}
                  <HeroCompany>{companyName}</HeroCompany> today?
                </>
              ) : (
                'What can I help you with today?'
              )}
            </HeroSubtitle>
          </HeroHeader>
          <InputShell $hero={showWelcome}>
            <MessageInput
              onSubmit={handleSend}
              disabled={isInputDisabled}
              placeholder={
                activeConversationId
                  ? 'Ask a follow-up question...'
                  : 'Ask anything about your business...'
              }
              hero={showWelcome}
              animatedPlaceholders={showWelcome ? placeholderQuestions : undefined}
            />
            <FooterRow $hero={showWelcome}>
              <Disclaimer $hero={showWelcome}>
                Ask Vantage uses AI. It may occasionally be wrong — verify important
                numbers.
              </Disclaimer>
              <TokenMeter
                variant="expanded"
                stats={tokenUsage}
                loading={tokenUsageLoading}
                isDemo={isDemoAccount}
                show={showWelcome}
              />
            </FooterRow>
          </InputShell>
          <SuggestionsGrid $show={showWelcome} aria-hidden={!showWelcome}>
            {chipQuestions.map((q) => (
              <SuggestionChip
                key={q}
                onClick={() => handleSend(q)}
                tabIndex={showWelcome ? 0 : -1}
              >
                {q}
              </SuggestionChip>
            ))}
          </SuggestionsGrid>
        </InputArea>
      </MainArea>
    </PageWrapper>
  );
};

// Time-of-day greeting; personalised with the user's first name when available.
function buildGreeting(firstName: string): string {
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return firstName ? `Good ${timeOfDay}, ${firstName}` : `Good ${timeOfDay}`;
}

// Render user+assistant message pairs from completed messages
function renderMessagePairs(
  messages: ReturnType<typeof useConversation>['state']['messages'],
  _lastUserMsgId: string | number | undefined,
  isStreaming: boolean,
  state: ReturnType<typeof useConversation>['state'],
  onFollowup: (q: string, meta?: FollowupMeta) => void,
  onVantageLink: (e: string, p: Record<string, string>) => void,
  onBuyTokens: () => void,
  onVote: (messageId: string | number, vote: MessageVote) => void,
  conversationId: number | null,
  lastUserMsgRef: React.RefObject<HTMLDivElement>,
  responseContainerRef: React.RefObject<HTMLDivElement>,
  assistantResponseTopRef: React.Ref<HTMLDivElement>
) {
  const pairs: React.ReactNode[] = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];

    if (msg.message_type === 'user') {
      const isLastUser = i === messages.length - 1 || messages[i + 1]?.message_type === 'user';
      pairs.push(
        <UserMessage
          key={`user-${msg.id}`}
          content={msg.content}
          scrollRef={isLastUser && isStreaming ? lastUserMsgRef : undefined}
        />
      );
    } else if (msg.message_type === 'assistant' && msg.response_version === 'v2_html') {
      const isLastDoneMessage = i === messages.length - 1 && !isStreaming;
      pairs.push(
        <AskVantageResponse
          key={`assistant-${msg.id}`}
          message={msg}
          streamStatus={isLastDoneMessage ? state.streamStatus : undefined}
          streamingContainerRef={isLastDoneMessage ? responseContainerRef : undefined}
          topAnchorRef={isLastDoneMessage ? assistantResponseTopRef : undefined}
          isStreaming={false}
          onFollowup={onFollowup}
          onVantageLink={onVantageLink}
          onBuyTokens={onBuyTokens}
          onVote={onVote}
          conversationId={conversationId}
        />
      );
    }
  }

  return pairs;
}


export default AskV2Container;

// ── Styled components ─────────────────────────────────────────────────────────

const navbarHeight = 60;
const maintenanceBannerHeight = 60;

const PageWrapper = styled.div.attrs(({ theme }) => ({
  'data-bs-theme': theme.mode === 'dark' ? 'dark' : 'light',
}))<{ $hasMaintenance: boolean }>`
  display: flex;
  position: fixed;
  top: ${({ $hasMaintenance }) =>
    $hasMaintenance ? navbarHeight + maintenanceBannerHeight : navbarHeight}px;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.av2.pageBg};
  color: ${({ theme }) => theme.av2.textPrimary};
  overflow: hidden;

  /* Assistant HTML: base + prose (override common LLM inline 12–13px on insight/callout <p>) */
  .av2-response {
    color: ${({ theme }) => theme.av2.responseText};
    font-size: 17px;
    line-height: 1.65;
  }

  .av2-response p,
  .av2-response li {
    font-size: 17px !important;
    line-height: 1.65 !important;
  }

  .av2-response td,
  .av2-response th {
    font-size: 16px !important;
  }

  .av2-response button[data-followup] {
    font-size: 14px !important;
    padding: 8px 16px !important;
  }

  /* Teaser card — small contextual upsell below a partial answer */
  .av2-response .av-teaser-card {
    margin: 16px 0;
    padding: 12px 14px;
    border-radius: 10px;
    background: linear-gradient(135deg, ${({ theme }) => theme.av2.primaryTint} 0%, ${({ theme }) => theme.av2.surfaceElevated} 60%);
    border: 1px dashed ${({ theme }) => theme.av2.primaryBorder};
    color: ${({ theme }) => theme.av2.textSecondary};
    font-size: 13px;
  }

  /* Business Profile deep-link button */
  .av2-response button[data-av-profile-open] {
    font-size: 13px;
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
    background: ${({ theme }) => theme.av2.primaryTintBg};
    color: ${({ theme }) => theme.av2.accentText};
    cursor: pointer;
  }

  /* Interactive what-if: visible “live” affordance (synthesis wraps with .av2-interactive-whatif) */
  .av2-response .av2-interactive-whatif {
    position: relative;
    margin: 20px 0;
    padding: 16px 18px 18px;
    border-radius: 12px;
    background: linear-gradient(
      165deg,
      ${({ theme }) => theme.av2.primaryTint} 0%,
      ${({ theme }) => theme.av2.surfaceElevated} 42%,
      ${({ theme }) =>
        theme.mode === 'dark'
          ? 'rgba(124, 92, 214, 0.06)'
          : 'rgba(59, 130, 246, 0.06)'} 100%
    );
    border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
    animation: av2InteractiveWhatifGlow 3s ease-in-out infinite;
  }

  @keyframes av2InteractiveWhatifGlow {
    0%,
    100% {
      box-shadow:
        0 0 0 1px rgba(59, 130, 246, 0.35),
        0 4px 22px ${({ theme }) => theme.av2.shadowWarm};
    }
    50% {
      box-shadow:
        0 0 0 1px rgba(59, 130, 246, 0.55),
        0 0 32px ${({ theme }) => theme.av2.primaryTintStrong},
        0 4px 22px ${({ theme }) => theme.av2.shadowWarm};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .av2-response .av2-interactive-whatif {
      animation: none;
      box-shadow:
        0 0 0 1px ${({ theme }) => theme.av2.primaryBorder},
        0 4px 20px ${({ theme }) => theme.av2.shadowWarm};
    }
  }

  .av2-response .av2-interactive-whatif-hint {
    font-size: 14px;
    color: ${({ theme }) => theme.av2.textSecondary};
    margin: 0 0 14px;
    line-height: 1.55;
    padding-bottom: 10px;
    border-bottom: 1px solid ${({ theme }) => theme.av2.borderSubtle};
  }

  .av2-response .av2-interactive-whatif-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.av2.accentText};
    margin: 0 0 10px;
  }

  .av2-response .av2-interactive-whatif-badge-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.av2.primary};
    flex-shrink: 0;
    animation: av2InteractiveWhatifDot 1.5s ease-in-out infinite;
  }

  @keyframes av2InteractiveWhatifDot {
    0%,
    100% {
      opacity: 0.5;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.12);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .av2-response .av2-interactive-whatif-badge-dot {
      animation: none;
      opacity: 1;
    }
  }

  ${({ theme }) => theme.mode === 'light' && av2ResponseLightModeHtmlFix}
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const TopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.pageBg};
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: start;
`;

const TopBarCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-self: end;
`;

const SidebarToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.av2.textPrimary};
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.hoverBg};
  }
`;

const HamburgerIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* Top-bar wordmark. Fades in only in chat mode — hero mode shows the larger
   HeroLogo above the greeting instead, so we don't show the brand twice.
   Timing matches the hero content fade (450ms) for a clean cross-fade. */
const TopBarLogo = styled.img<{ $show: boolean }>`
  height: 28px;
  width: auto;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? '0' : '-4px')});
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transition: opacity 450ms ease ${({ $show }) => ($show ? '120ms' : '0ms')},
    transform 450ms cubic-bezier(0.22, 1, 0.36, 1)
      ${({ $show }) => ($show ? '120ms' : '0ms')};
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.av2.textMuted};
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.av2.hoverBg};
    color: ${({ theme }) => theme.av2.textPrimary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PrintIcon: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const NewChatTopBtn = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.av2.primary};
  background: transparent;
  color: ${({ theme }) => theme.av2.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.primaryTint};
  }
`;

const ProfileButton = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  background: transparent;
  color: ${({ theme }) => theme.av2.accentText};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.primaryTint};
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  ${contentColumnPadding}
`;

const MessagesInner = styled.div`
  padding: 20px 0 32px;
`;

const InputArea = styled.div<{ $hero?: boolean }>`
  flex-shrink: 0;
  flex-grow: ${({ $hero }) => ($hero ? 1 : 0)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $hero }) => ($hero ? 'center' : 'flex-end')};
  gap: ${({ $hero }) => ($hero ? '36px' : '0')};
  padding: ${({ $hero }) => ($hero ? '24px 16px' : '0 0 6px')};
  overflow-y: ${({ $hero }) => ($hero ? 'auto' : 'visible')};
  min-height: 0;
  background: ${({ theme, $hero }) =>
    $hero
      ? heroAmbientBackground((theme.mode as ThemeMode) || 'light')
      : theme.av2.pageBg};
  transition: flex-grow 600ms cubic-bezier(0.22, 1, 0.36, 1),
    gap 500ms cubic-bezier(0.22, 1, 0.36, 1),
    padding 500ms cubic-bezier(0.22, 1, 0.36, 1),
    background 500ms ease;

  /* Chat mode: match the main message column width */
  ${({ $hero }) => !$hero && contentColumnPadding}

  @media (min-width: 769px) {
    justify-content: ${({ $hero }) => ($hero ? 'flex-start' : 'flex-end')};
    padding-top: ${({ $hero }) => ($hero ? '12vh' : '0')};
    padding-bottom: ${({ $hero }) => ($hero ? '4vh' : '6px')};
    ${({ $hero }) =>
      $hero &&
      css`
        padding-left: 16px;
        padding-right: 16px;
      `}
  }
`;

const HeroHeader = styled.div<{ $show: boolean }>`
  text-align: center;
  max-width: 720px;
  width: 100%;
  overflow: hidden;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? '0' : '-16px')});
  /* Room for logo + greeting + subtitle with generous spacing. */
  max-height: ${({ $show }) => ($show ? '360px' : '0')};
  transition: opacity 450ms ease ${({ $show }) => ($show ? '120ms' : '0ms')},
    transform 500ms cubic-bezier(0.22, 1, 0.36, 1)
      ${({ $show }) => ($show ? '120ms' : '0ms')},
    max-height 500ms cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
`;

const HeroLogo = styled.img`
  height: 44px;
  width: auto;
  margin: 0 auto 36px;
  display: block;
  filter: drop-shadow(
    0 4px 18px
      ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.28)' : theme.av2.shadowAccent}
  );
  opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.95 : 1)};

  @media (max-width: 640px) {
    height: 38px;
    margin-bottom: 28px;
  }
`;

const HeroGreeting = styled.h1`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 14px;
  padding: 2px 0 6px;
  background: ${brandGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 26px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.av2.textSecondary};
  margin: 0;
`;

const HeroCompany = styled.strong`
  color: ${({ theme }) => theme.av2.textSecondary};
  font-weight: 600;
`;

const InputShell = styled.div<{ $hero?: boolean }>`
  width: 100%;
  max-width: ${({ $hero }) => ($hero ? '760px' : 'none')};
  min-width: 0;
  overflow: hidden;
  transition: max-width 400ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const FooterRow = styled.div<{ $hero: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $hero }) => ($hero ? 'space-between' : 'center')};
  gap: 12px;
  margin-top: 6px;
  padding: 0 2px;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    flex-direction: ${({ $hero }) => ($hero ? 'column' : 'row')};
    gap: ${({ $hero }) => ($hero ? '6px' : '12px')};
    align-items: center;
  }
`;

const Disclaimer = styled.p<{ $hero?: boolean }>`
  font-size: 11px;
  color: ${({ theme, $hero }) =>
    theme.mode === 'dark' && $hero ? theme.av2.textPrimary : theme.av2.textSecondary};
  opacity: ${({ theme, $hero }) => (theme.mode === 'dark' && $hero ? 0.82 : 1)};
  text-align: ${({ $hero }) => ($hero ? 'left' : 'center')};
  margin: 0;
  letter-spacing: 0.01em;
  flex: 1 1 0;
  min-width: 0;
  text-shadow: ${({ theme, $hero }) =>
    theme.mode === 'dark' && $hero ? '0 1px 3px rgba(0, 0, 0, 0.55)' : 'none'};

  @media (max-width: 640px) {
    text-align: center;
    flex: 0 1 auto;
  }
`;

const StreamingArea = styled.div`
  min-height: 20px;
`;

const streamingDotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.65); opacity: 0.45; }
  40% { transform: scale(1); opacity: 1; }
`;

const LiveStreamFooter = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.surfaceElevated};
`;

const LiveDots = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LiveDot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.av2.primary};
  animation: ${streamingDotPulse} 1.1s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

const LiveText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.av2.textSecondary};
`;

const SuggestionsGrid = styled.div<{ $show: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
  overflow: hidden;
  padding: 4px 0;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? '0' : '8px')});
  max-height: ${({ $show }) => ($show ? '300px' : '0')};
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transition: opacity 450ms ease ${({ $show }) => ($show ? '220ms' : '0ms')},
    transform 500ms cubic-bezier(0.22, 1, 0.36, 1)
      ${({ $show }) => ($show ? '220ms' : '0ms')},
    max-height 500ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const SuggestionChip = styled.button`
  padding: 9px 16px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  background: ${({ theme }) => theme.av2.surfaceElevated};
  color: ${({ theme }) => theme.av2.textSecondary};
  font-size: 13px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease,
    transform 0.15s ease;
  backdrop-filter: blur(4px);

  &:hover {
    border-color: ${({ theme }) => theme.av2.primary};
    background: ${({ theme }) => theme.av2.primaryTint};
    color: ${({ theme }) => theme.av2.textPrimary};
    transform: translateY(-1px);
  }
`;
